import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import AuthService from "@services/AuthService";
import axios from "axios";
import Cookies from "js-cookie";

const initialState = {
  consent: false,
  auth: false,
  isLoading: true,
  id: null,
  demo: null,
  email: "",
  name: "",
  lastname: "",
  product_line: "",
  password: "",
  role: "",
  cover: "",
  badges: [],
  error: "",
  serverStatus: "",
};

const UNAUTHORIZED_ERROR = "Unauthorized!";
const REFRESH_TOKEN_ERROR = "Failed to get refresh token!";
const DEFAULT_ERROR = "Error server!";

export const signIn = createAsyncThunk(
  "auth/signIn",
  async ({email, password}) => {
    try {
      const response = await AuthService.signIn(email, password);

      Cookies.set("refresh", response.data.rt);
      Cookies.set("jwt", response.data.jwt);

      return response.data.user;
    } catch (err) {
      console.log("err", err);
      throw new Error(UNAUTHORIZED_ERROR);
    }
  },
);

export const signOut = createAsyncThunk("auth/signOut", async () => {
  try {
    const rt = Cookies.get("refresh");

    if (rt) {
      await AuthService.signOut(rt);

      Cookies.remove("refresh");
      Cookies.remove("jwt");
    } else {
      throw new Error(DEFAULT_ERROR);
    }
  } catch (err) {
    console.log("err", err);
    throw new Error(UNAUTHORIZED_ERROR);
  }
});

export const checkAuth = createAsyncThunk("auth/checkAuth", async () => {
  try {
    const rt = Cookies.get("refresh");

    if (rt) {
      const response = await axios.post("/jwt", {rt}, {withCredentials: true});

      Cookies.set("refresh", response.data.rt);
      Cookies.set("jwt", response.data.jwt);

      return response.data.user;
    } else {
      throw new Error(DEFAULT_ERROR);
    }
  } catch (err) {
    console.log("err", err);
    throw new Error(REFRESH_TOKEN_ERROR);
  }
});

const candidateSlice = createSlice({
  name: "auth-candidate",
  initialState,
  reducers: {
    setEmail(state, action) {
      state.email = action.payload;
    },
    setPassword(state, action) {
      state.password = action.payload;
    },
    toggleConsent(state) {
      state.consent = !state.consent;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setServerStatus(state, action) {
      state.serverStatus = action.payload;
    },
    clearCandidate() {
      return {
        ...initialState,
        isLoading: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(signIn.fulfilled, (state, action) => {
        Object.assign(state, {
          auth: true,
          consent: false,
          id: action.payload.id,
          demo: action.payload.demo ? action.payload.demo : null,
          email: action.payload.email,
          password: "",
          name: action.payload.name,
          lastname: action.payload.lastname,
          product_line: action.payload.product_line,
          role: action.payload.type,
          cover: action.payload.cover,
          badges: action.payload.badges,
          error: "",
          serverStatus: "success",
        });
      })
      .addCase(signIn.rejected, (state) => {
        state.error = UNAUTHORIZED_ERROR;
        state.serverStatus = "error";
      })

      .addCase(signOut.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(signOut.fulfilled, (state) => {
        Object.assign(state, {
          ...initialState,
          isLoading: false,
        });
      })
      .addCase(signOut.rejected, (state) => {
        state.error = UNAUTHORIZED_ERROR;
        state.serverStatus = "error";
        state.isLoading = false;
      })

      .addCase(checkAuth.pending, (state) => {
        state.serverStatus = "loading";
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        Object.assign(state, {
          auth: true,
          isLoading: false,
          consent: false,
          email: action.payload.email,
          id: action.payload.id,
          demo: action.payload.demo ? action.payload.demo : null,
          name: action.payload.name,
          lastname: action.payload.lastname,
          product_line: action.payload.product_line,
          password: "",
          role: action.payload.type,
          cover: action.payload.cover,
          badges: action.payload.badges,
          error: "",
          serverStatus: "success",
        });
      })
      .addCase(checkAuth.rejected, (state) => {
        state.error = "";
        state.serverStatus = "";
        state.isLoading = false;
      });
  },
});

export const {
  setEmail,
  setPassword,
  toggleConsent,
  setError,
  setServerStatus,
  clearCandidate,
} = candidateSlice.actions;
export default candidateSlice.reducer;
